/* eslint-disable react/jsx-pascal-case */
import React, { useCallback, useEffect, useState } from "react";
import { Input, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Auth } from "../../../config/aws-exports";
import { updateTeamMembers } from "../../../redux/actions";
import TopAlert from "../../../components/TopNotification/TopAlert";
import "./Add Member.css";

const PASSWORD_PATTERN = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/;

// One modal for both flows — mode: "activate" | "changePassword".
// Same fields (read-only email, password, confirm password); only the
// title, button label and submit behavior differ.
function MemberPasswordModal({ open, mode, member, onClose, onSuccess }) {
  const isActivate = mode === "activate";
  const title = isActivate ? "Activate Member" : "Change Password";
  const buttonLabel = isActivate ? "Activate" : "Change Password";

  const dispatch = useDispatch();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null); // { type, message }
  const [awaitingTeamUpdate, setAwaitingTeamUpdate] = useState(false);
  const [teamUpdateStarted, setTeamUpdateStarted] = useState(false);

  const updateLoading = useSelector((state) => state.updateTeamMembersReducer.loading);
  const updateSuccess = useSelector((state) => state.updateTeamMembersReducer.success);
  const updateError = useSelector((state) => state.updateTeamMembersReducer.error);

  const invitationId = member?.invitationId || member?.InvitationId;
  const memberId = invitationId || member?._id || member?.id;
  const email = member?.EmailAddress || member?.emailAddress || member?.email || "";
  const fullName = member?.FullName || member?.fullName || "";
  const jobTitle = member?.JobTitle || member?.jobTitle || "";

  const resetAndClose = useCallback(() => {
    setPassword("");
    setConfirmPassword("");
    setErrors({});
    setAlert(null);
    setLoading(false);
    setAwaitingTeamUpdate(false);
    setTeamUpdateStarted(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!awaitingTeamUpdate) return;

    if (updateLoading) {
      setTeamUpdateStarted(true);
      return;
    }

    if (!teamUpdateStarted) return;

    if (updateSuccess) {
      setAlert({
        type: "success",
        message: isActivate
          ? "Team member activated successfully"
          : "Password changed successfully",
      });
      setTimeout(() => {
        resetAndClose();
        onSuccess?.();
      }, 2000);
      return;
    }

    if (updateError) {
      setAlert({ type: "error", message: "Something went wrong!" });
      setLoading(false);
      setAwaitingTeamUpdate(false);
      setTeamUpdateStarted(false);
    }
  }, [
    awaitingTeamUpdate,
    isActivate,
    onSuccess,
    resetAndClose,
    teamUpdateStarted,
    updateError,
    updateLoading,
    updateSuccess,
  ]);

  function validate() {
    const errs = {};
    if (!password) {
      errs.password = "Please add password";
    } else if (!PASSWORD_PATTERN.test(password)) {
      errs.password =
        "Password must contain at least 8 characters. Combine numbers, upper and lowercase letters.";
    }
    if (!confirmPassword) {
      errs.confirmPassword = "Please confirm password";
    } else if (password && confirmPassword !== password) {
      errs.confirmPassword = "Passwords do not match";
    }
    return errs;
  }

  async function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    setAlert(null);

    if (isActivate) {
      try {
        await Auth.signUp({
          username: email,
          password,
          attributes: {
            email,
            "custom:account_name": fullName,
          },
        });
        setAwaitingTeamUpdate(true);
        setTeamUpdateStarted(true);
        dispatch(
          updateTeamMembers(memberId, {
            FullName: fullName,
            EmailAddress: email,
            JobTitle: jobTitle,
          })
        );
      } catch (e) {
        setAlert({
          type: "error",
          message: "An account with the given email already exists",
        });
        setLoading(false);
      }
    } else {
      if (!invitationId) {
        setAlert({ type: "error", message: "Invitation id not found" });
        setLoading(false);
        return;
      }

      setAwaitingTeamUpdate(true);
      setTeamUpdateStarted(true);
      dispatch(
        updateTeamMembers(
          invitationId,
          {
            Password: password,
            ConfirmPassword: confirmPassword,
          },
          { changePassword: true }
        )
      );
    }
  }

  const eyeRender = (visible) =>
    visible ? <EyeOutlined /> : <EyeInvisibleOutlined />;

  return (
    <Modal
      open={open}
      onCancel={resetAndClose}
      footer={null}
      width={480}
      centered
      className="add-member-modal"
      destroyOnClose
    >
      <div className="am-form">
        {alert && (
          <TopAlert display="block" title={alert.message} type={alert.type} />
        )}

        <h2 className="am-title">{title}</h2>
        <hr className="am-divider" />

        <p className="mp-description">
          Share this password with the team member so they can sign in
          successfully.
        </p>

        {/* Work Email — read-only */}
        <div className="am-field">
          <label className="am-label">
            Work Email <span className="am-required">*</span>
          </label>
          <Input className="am-input am-email-disabled" value={email} disabled />
        </div>

        {/* Password */}
        <div className="am-field">
          <label className="am-label">Password</label>
          <Input.Password
            className={`am-input ${errors.password ? "am-input-error" : ""}`}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => ({ ...prev, password: "" }));
            }}
            iconRender={eyeRender}
          />
          {errors.password && (
            <span className="am-err-text">{errors.password}</span>
          )}
        </div>

        {/* Confirm Password */}
        <div className="am-field">
          <label className="am-label">Confirm Password</label>
          <Input.Password
            className={`am-input ${errors.confirmPassword ? "am-input-error" : ""}`}
            placeholder="Enter your password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors((prev) => ({ ...prev, confirmPassword: "" }));
            }}
            iconRender={eyeRender}
          />
          {errors.confirmPassword && (
            <span className="am-err-text">{errors.confirmPassword}</span>
          )}
        </div>

        <button
          type="button"
          className="am-submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <LoadingOutlined />
          ) : (
            <>
              <CheckCircleOutlined /> {buttonLabel}
            </>
          )}
        </button>
      </div>
    </Modal>
  );
}

export default MemberPasswordModal;
